import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Camera } from '@babylonjs/core/Cameras/camera';
import type { Vec3 } from '../types';
import { M_TO_YARDS } from '../constants';
import { fitFlight, type FlightView, type FrameWindow } from './FlightFraming';

export type CameraMode = 'freeLook' | 'behind' | 'side' | 'top' | 'replay' | 'transitioning' | 'broadcast';
export type MobileShotPresentationPhase = 'baseline' | 'spec' | 'gear' | 'ball';

const CAMERA_LABELS: Record<CameraMode, string> = {
  freeLook: 'FREE',
  behind: 'BEHIND',
  side: 'SIDE',
  top: 'TOP',
  replay: 'REPLAY',
  transitioning: 'MOVING',
  broadcast: 'LIVE',
};

interface MobileShotViewPreset {
  pos: Vector3;
  target: Vector3;
  fov: number;
}

function getMobileShotViewPreset(
  phase: MobileShotPresentationPhase,
  clubKey: string,
): MobileShotViewPreset {
  const isWedge = /wedge|sw|gw|lw|pw$/i.test(clubKey);
  const isIron = isWedge || /iron|[0-9]i$/i.test(clubKey);

  if (isWedge) {
    return {
      pos: new Vector3(-190, 28, 58),
      target: new Vector3(0, 12, 58),
      fov: 0.78,
    };
  }

  if (isIron) {
    return {
      pos: new Vector3(-230, 32, 82),
      target: new Vector3(0, 10, 82),
      fov: 0.80,
    };
  }

  if (phase === 'gear' || phase === 'ball') {
    return {
      pos: new Vector3(-270, 34, 118),
      target: new Vector3(0, 9, 118),
      fov: 0.84,
    };
  }

  return {
    pos: new Vector3(-260, 34, 112),
    target: new Vector3(0, 9, 112),
    fov: 0.82,
  };
}

// ── Easing ──────────────────────────────────────────────────────────────────
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * World-class multi-angle camera system for a golf launch monitor.
 *
 * Features:
 *   - Animated transitions with cubic easing
 *   - Touch-optimized: 1-finger orbit, 2-finger pan, pinch zoom, inertia
 *   - Phase-specific cinematic intros (unique angle per phase)
 *   - Auto-framing: camera adapts to frame all landing positions
 *   - Orbit anchor: pivot grounded to tee/landing zone
 *   - Broadcast director: auto-cuts behind → side → overhead during flight
 *   - Double-tap to reset to phase default view
 *   - Chase camera replay
 */
export class CameraSystem {
  private flightPoints: Vec3[] = [];
  private flightView: FlightView = 'behind';
  private manualFraming = false;
  private flightFramePending = false;
  private fittedFrame: ReturnType<typeof fitFlight> = null;
  getFrameWindow: (() => FrameWindow) | null = null;

  beginFlightPresentation(): void {
    this.flightPoints = [];
    this.flightView = 'behind';
    this.manualFraming = false;
    this.flightFramePending = true;
    this.fittedFrame = null;
  }

  setFlightPoints(points: Vec3[]): void {
    const first = this.flightPoints.length === 0;
    this.flightPoints = points;
    if (first && points.length && this.flightFramePending) this.fitFlights();
    else if (!this.manualFraming && this.fittedFrame && points.length) {
      const frame = this.fittedFrame;
      const engine = this.scene.getEngine();
      const aspect = engine.getRenderWidth() / engine.getRenderHeight();
      const window = this.getFrameWindow?.() ?? { left: -.88, right: .88, bottom: -.82, top: .82 };
      const outside = points.some(p => {
        const v = new Vector3(p.x - frame.position.x, p.y - frame.position.y, p.z - frame.position.z);
        const depth = v.x * frame.forward.x + v.y * frame.forward.y + v.z * frame.forward.z;
        const x = (v.x * frame.right.x + v.y * frame.right.y + v.z * frame.right.z) / (depth * Math.tan(frame.fov / 2) * aspect);
        const y = (v.x * frame.up.x + v.y * frame.up.y + v.z * frame.up.z) / (depth * Math.tan(frame.fov / 2));
        return depth <= 0 || x < window.left || x > window.right || y < window.bottom || y > window.top;
      });
      if (outside) this.fitFlights();
    }
  }

  /** Explicit reset; automatic panel/viewport changes never override a user's orbit. */
  fitFlights(view: FlightView = this.flightView, animate = true): void {
    this.flightView = view;
    const engine = this.scene.getEngine();
    const fit = fitFlight(this.flightPoints, view, engine.getRenderWidth() / engine.getRenderHeight(),
      this.getFrameWindow?.());
    if (!fit) return;
    this.fittedFrame = fit;
    this.manualFraming = false;
    this.flightFramePending = false;
    const pos = new Vector3(fit.position.x, fit.position.y, fit.position.z);
    const target = new Vector3(fit.target.x, fit.target.y, fit.target.z);
    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;
    this.setPhaseDefault(pos, target, fit.fov);
    this.animateTo(pos, target, fit.fov, animate ? .55 : .001, 'freeLook');
  }

  refreshFlightLayout(): void {
    if (!this.manualFraming && this.flightPoints.length) this.fitFlights();
  }
  private scene: Scene;
  private camera: ArcRotateCamera;
  private mode: CameraMode = 'freeLook';

  // Smoothed camera state
  private smoothedPosition = new Vector3(0, 5, -10);
  private smoothedTarget = new Vector3(0, 0, 10);

  // Track max ball height + max distance for framing
  private maxBallHeight = 0;
  private maxBallZ = 0;

  // Behind-view base position
  private behindOffsetX = 0;
  private behindBaseY = 3.5;
  private behindBaseZ = -8;

  // Keyboard flying
  private activeKeys = new Set<string>();
  private readonly FLY_SPEED = 60;

  // ── Animated transition state ───────────────────────────────────────────
  private transitionActive = false;
  private transitionStartPos = new Vector3();
  private transitionEndPos = new Vector3();
  private transitionStartTarget = new Vector3();
  private transitionEndTarget = new Vector3();
  private transitionStartFov = 0.8;
  private transitionEndFov = 0.8;
  private transitionProgress = 0;
  private transitionDuration = 0.8;
  private transitionPostMode: CameraMode = 'freeLook';
  private transitionCallback: (() => void) | null = null;

  // ── Phase default (for double-tap reset) ────────────────────────────────
  private phaseDefault: { pos: Vector3; target: Vector3; fov: number } | null = null;

  // ── Double-tap detection ────────────────────────────────────────────────
  private lastTapTime = 0;
  private lastTapX = 0;
  private lastTapY = 0;

  // ── Orbit anchor ────────────────────────────────────────────────────────
  private _orbitAnchor: Vector3 | null = null;

  // ── Broadcast director state ────────────────────────────────────────────
  private broadcastElapsed = 0;
  private broadcastCutPhase: 'launch' | 'ascent' | 'descent' | 'landed' = 'launch';
  private broadcastApexReached = false;

  constructor(scene: Scene) {
    this.scene = scene;

    this.camera = new ArcRotateCamera(
      'camera',
      -Math.PI / 2,
      Math.PI / 4,
      15,
      new Vector3(0, 0, 5),
      scene,
    );

    // ── Radius / angle limits ──
    this.camera.lowerRadiusLimit = 2;
    this.camera.upperRadiusLimit = 3000;
    this.camera.lowerBetaLimit = 0.01;
    this.camera.upperBetaLimit = Math.PI / 2 - 0.05;

    // ── Touch-optimized controls ──
    this.camera.wheelPrecision = 20;
    this.camera.wheelDeltaPercentage = 0.02;
    this.camera.pinchPrecision = 40;
    this.camera.pinchDeltaPercentage = 0.002;
    this.camera.panningSensibility = 100;
    this.camera.angularSensibilityX = 1000;
    this.camera.angularSensibilityY = 1000;
    this.camera.inertia = 0.88;
    this.camera.useNaturalPinchZoom = true;
    (this.camera as any).multiTouchPanAndZoom = true;
    (this.camera as any).multiTouchPanning = true;

    this.camera.attachControl(scene.getEngine().getRenderingCanvas()!, true);

    // Disable built-in camera keyboard
    this.camera.keysUp = [];
    this.camera.keysDown = [];
    this.camera.keysLeft = [];
    this.camera.keysRight = [];

    this.camera.setPosition(new Vector3(0, 3.5, -8));
    this.camera.setTarget(new Vector3(0, 0.5, 10));
    scene.activeCamera = this.camera;

    // Keyboard listeners
    document.addEventListener('keydown', (e) => {
      this.activeKeys.add(e.code);
      if (e.code.startsWith('Arrow')) {
        const tag = (document.activeElement?.tagName || '');
        if (tag !== 'INPUT' && tag !== 'SELECT' && tag !== 'TEXTAREA') {
          e.preventDefault();
        }
      }
    });
    document.addEventListener('keyup', (e) => this.activeKeys.delete(e.code));

    // ── Double-tap to reset (touch) ──
    const canvas = scene.getEngine().getRenderingCanvas()!;
    canvas.addEventListener('pointerdown', () => { this.manualFraming = true; });
    canvas.addEventListener('wheel', () => { this.manualFraming = true; }, { passive: true });
    canvas.addEventListener('touchend', (e: TouchEvent) => {
      if (e.changedTouches.length !== 1) return;
      const touch = e.changedTouches[0];
      const now = Date.now();
      const dx = touch.clientX - this.lastTapX;
      const dy = touch.clientY - this.lastTapY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (now - this.lastTapTime < 300 && dist < 30) {
        e.preventDefault();
        this.resetToPhaseDefault();
      }
      this.lastTapTime = now;
      this.lastTapX = touch.clientX;
      this.lastTapY = touch.clientY;
    }, { passive: false });

    // Double-click to reset (desktop)
    canvas.addEventListener('dblclick', (e) => {
      e.preventDefault();
      this.resetToPhaseDefault();
    });

    // Per-frame processing
    scene.onBeforeRenderObservable.add(() => {
      const dt = scene.getEngine().getDeltaTime() / 1000;
      this.processKeyboard(dt);
      this.processTransition(dt);
      this.processIntroHold(dt);
      // Keep digital tracers legible when fitting long flights in narrow viewports.
      scene.fogDensity = .0018 * Math.min(1, 250 / Math.max(250, this.camera.radius));
      this.updateDebugOverlay();
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ── ANIMATED TRANSITION ENGINE ────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  animateTo(
    position: Vector3,
    target: Vector3,
    fov: number,
    duration: number,
    postMode: CameraMode,
    callback?: () => void,
  ): void {
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.transitionActive = false;
      this.camera.setPosition(position);
      this.camera.setTarget(target);
      this.camera.fov = fov;
      this.smoothedPosition.copyFrom(position);
      this.smoothedTarget.copyFrom(target);
      this.mode = postMode;
      this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
      callback?.();
      return;
    }
    this.transitionStartPos.copyFrom(this.camera.position);
    this.transitionStartTarget.copyFrom(this.camera.getTarget());
    this.transitionEndPos.copyFrom(position);
    this.transitionEndTarget.copyFrom(target);
    this.transitionStartFov = this.camera.fov;
    this.transitionEndFov = fov;
    this.transitionProgress = 0;
    this.transitionDuration = duration;
    this.transitionPostMode = postMode;
    this.transitionCallback = callback || null;
    this.transitionActive = true;
    this.mode = 'transitioning';
    this.camera.detachControl();
  }

  /** Convenience: animateTo using plain Vec3 objects. */
  animateToVec(pos: Vec3, target: Vec3, fov: number, duration: number, postMode: CameraMode, callback?: () => void): void {
    this.animateTo(
      new Vector3(pos.x, pos.y, pos.z),
      new Vector3(target.x, target.y, target.z),
      fov, duration, postMode, callback,
    );
  }

  private processTransition(dt: number): void {
    if (!this.transitionActive) return;

    this.transitionProgress += dt / this.transitionDuration;
    if (this.transitionProgress >= 1) {
      this.transitionProgress = 1;
      this.transitionActive = false;
    }

    const t = easeInOutCubic(this.transitionProgress);
    const pos = Vector3.Lerp(this.transitionStartPos, this.transitionEndPos, t);
    const tgt = Vector3.Lerp(this.transitionStartTarget, this.transitionEndTarget, t);
    this.camera.fov = this.transitionStartFov + (this.transitionEndFov - this.transitionStartFov) * t;

    this.camera.setPosition(pos);
    this.camera.setTarget(tgt);
    this.smoothedPosition.copyFrom(pos);
    this.smoothedTarget.copyFrom(tgt);

    if (!this.transitionActive) {
      this.mode = this.transitionPostMode;
      this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
      this.transitionCallback?.();
      this.transitionCallback = null;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ── PHASE-SPECIFIC CINEMATIC INTROS ───────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Phase-specific cinematic intro — each phase gets a unique camera angle.
   * Call this instead of flybyToDownrange() for variety.
   */
  // ── Delayed dynamic flyby state ──────────────────────────────────────────
  private introHoldTimer = 0;
  private introHolding = false;
  private introCallback: (() => void) | null = null;
  /** External provider of ball positions — set by UIManager. */
  getBallPositions: (() => Vec3[]) | null = null;

  /**
   * Phase-specific cinematic intro.
   * Holds at behind-tee view for 1s, then dynamically flies
   * to a position computed from actual ball positions.
   */
  phaseIntro(_phase: string, onComplete?: () => void): void {
    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;

    // Set the starting angle and hand control to the user — no flyby
    const pos = new Vector3(18.8, 15.8, -58.4);
    const target = new Vector3(3.3, -2.9, 30.2);
    this.setStartPosition(pos, target);
    this.camera.fov = 1.0;
    this.setPhaseDefault(pos, target, 1.0);

    this.mode = 'freeLook';
    this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
    onComplete?.();
  }

  /** Called every frame — handles the delayed intro transition. */
  private processIntroHold(dt: number): void {
    if (!this.introHolding) return;

    this.introHoldTimer += dt;
    if (this.introHoldTimer < 1.0) return; // hold for 1s

    this.introHolding = false;

    // Compute dynamic end position from actual ball positions
    const positions = this.getBallPositions?.() || [];
    const endPos = this.computeDynamicEndPos(positions);
    const endTarget = this.computeDynamicEndTarget(positions);

    this.setPhaseDefault(endPos, endTarget, 1.0);
    this.animateTo(endPos, endTarget, 1.0, 2.0, 'freeLook', this.introCallback ?? undefined);
    this.introCallback = null;
  }

  /**
   * Compute a camera position that keeps all balls in view.
   * Scales height and Z offset based on how far downrange balls are.
   */
  private computeDynamicEndPos(positions: Vec3[]): Vector3 {
    if (positions.length === 0) {
      // Fallback — assume ~200m range
      return new Vector3(20, 45, 50);
    }

    let maxZ = 0, maxY = 0;
    for (const p of positions) {
      if (p.z > maxZ) maxZ = p.z;
      if (p.y > maxY) maxY = p.y;
    }

    // Scale camera position based on ball range
    // At maxZ=200m: height ~46, z ~50. At maxZ=100m: height ~30, z ~25
    const rangeScale = Math.max(maxZ / 200, 0.4); // clamp minimum
    const height = 20 + rangeScale * 30 + Math.max(maxY * 0.3, 0);
    const z = maxZ * 0.25;  // sit about 25% downrange
    const x = 20 * rangeScale;

    return new Vector3(x, height, z);
  }

  /** Compute a look-at target centered on the ball action. */
  private computeDynamicEndTarget(positions: Vec3[]): Vector3 {
    if (positions.length === 0) {
      return new Vector3(8, 7, 120);
    }

    let avgX = 0, avgZ = 0, maxZ = 0;
    for (const p of positions) {
      avgX += p.x;
      avgZ += p.z;
      if (p.z > maxZ) maxZ = p.z;
    }
    avgX /= positions.length;
    avgZ /= positions.length;

    // Look slightly ahead of center — between average and max
    const lookZ = avgZ * 0.4 + maxZ * 0.6;
    return new Vector3(avgX * 0.5, 5, lookZ);
  }

  /**
   * Cinematic flyby on first load.
   * Start: elevated behind-tee angle looking downrange.
   * End: higher/further position keeping balls in view.
   */
  flybyIntro(onComplete?: () => void): void {
    // Set the starting angle — no animation, just place the camera
    const pos = new Vector3(18.8, 15.8, -58.4);
    const target = new Vector3(3.3, -2.9, 30.2);
    this.setStartPosition(pos, target);
    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;
    this.camera.fov = 1.0;
    this.setPhaseDefault(pos, target, 1.0);
    this.mode = 'freeLook';
    this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
    onComplete?.();
  }

  flybyToDownrange(duration = 1.2, onComplete?: () => void): void {
    this.maxBallHeight = 0;
    this.maxBallZ = 0;
    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;

    const positions = this.getBallPositions?.() || [];
    const endPos = this.computeDynamicEndPos(positions);
    const endTarget = this.computeDynamicEndTarget(positions);
    this.setPhaseDefault(endPos, endTarget, 1.0);
    this.animateTo(endPos, endTarget, 1.0, duration, 'freeLook', onComplete);
  }

  flybyToSide(duration = 1.0, onComplete?: () => void): void {
    this.camera.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;
    this.animateTo(
      new Vector3(-280, 40, 90),
      new Vector3(0, 8, 90),
      0.75,
      duration,
      'side',
      onComplete,
    );
  }

  flybyOrbitSweep(startAngle: number, endAngle: number, radius: number, height: number, targetZ: number, duration = 2.0, onComplete?: () => void): void {
    const startPos = new Vector3(
      Math.sin(startAngle) * radius,
      height,
      targetZ + Math.cos(startAngle) * radius * 0.3,
    );
    const endPos = new Vector3(
      Math.sin(endAngle) * radius,
      height,
      targetZ + Math.cos(endAngle) * radius * 0.3,
    );
    const target = new Vector3(0, 3, targetZ);

    this.camera.setPosition(startPos);
    this.camera.setTarget(target);
    this.smoothedPosition.copyFrom(startPos);
    this.smoothedTarget.copyFrom(target);
    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;

    this.animateTo(endPos, target, 0.85, duration, 'freeLook', onComplete);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ── AUTO-FRAMING ──────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Frame all landing points from a classic TV-style side profile.
   * Camera sits far to the left, elevated, looking at the midpoint between
   * tee and the furthest ball. Full arc (tee → apex → landing) is visible.
   */
  framePoints(points: Vec3[], duration = 1.2, onComplete?: () => void): void {
    if (points.length === 0) return;

    // Find furthest ball downrange
    let maxZ = 0;
    let minX = Infinity, maxX = -Infinity;
    for (const p of points) {
      if (p.z > maxZ) maxZ = p.z;
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
    }

    const midX = (minX + maxX) / 2;
    // Center between tee (z=0) and furthest landing
    const midZ = maxZ * 0.45;

    // Pull back far enough to see the full trajectory arc
    // At 200m range, we need ~300m of camera distance to see the whole arc
    const camDist = Math.max(maxZ * 1.5, 300);
    const camHeight = Math.max(maxZ * 0.18, 30);

    const pos = new Vector3(-camDist, camHeight, midZ);
    const target = new Vector3(midX, maxZ * 0.06, midZ);

    this.camera.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;
    this.setPhaseDefault(pos, target, 0.7);
    this.setOrbitAnchor(target);
    this.animateTo(pos, target, 0.7, duration, 'freeLook', onComplete);
  }

  /**
   * Frame landing positions from a tight side view.
   * Used for ball results where we care about distance, not dispersion.
   */
  /**
   * Side profile for ball results — all balls on same line,
   * framing from tee through furthest landing with full arc visible.
   */
  frameSide(points: Vec3[], duration = 1.2, onComplete?: () => void): void {
    if (points.length === 0) return;

    let maxZ = 0;
    for (const p of points) {
      if (p.z > maxZ) maxZ = p.z;
    }

    // Center between tee and furthest ball, camera far to the left
    const midZ = maxZ * 0.45;
    const camDist = Math.max(maxZ * 1.4, 280);

    const pos = new Vector3(-camDist, 25, midZ);
    const target = new Vector3(0, maxZ * 0.05, midZ);

    this.camera.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;
    this.setPhaseDefault(pos, target, 0.7);
    this.setOrbitAnchor(target);
    this.animateTo(pos, target, 0.7, duration, 'freeLook', onComplete);
  }

  /**
   * Smart top-down view — frames from tee box through all landing positions.
   * Zooms out to accommodate the actual shot spread, not a fixed position.
   */
  smartTopView(points: Vec3[], duration = 0.7, onComplete?: () => void): void {
    if (points.length === 0) {
      // Fallback to default top view
      this.setMobileTopView(true);
      return;
    }

    let minX = Infinity, maxX = -Infinity, maxZ = -Infinity;
    for (const p of points) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.z > maxZ) maxZ = p.z;
    }

    // Frame from origin to furthest ball, padded
    const midX = (minX + maxX) / 2;
    const midZ = maxZ / 2;
    const spreadX = (maxX - minX) * M_TO_YARDS;
    const spreadZ = maxZ * M_TO_YARDS;
    const maxSpread = Math.max(spreadX, spreadZ, 50);

    // Height scales with spread — higher = sees more
    const camHeight = Math.max(80, maxSpread * 0.6);
    const pos = new Vector3(midX, camHeight, midZ);
    const target = new Vector3(midX, 0, midZ);

    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;
    const fov = Math.min(1.0, Math.max(0.6, 0.7 + maxSpread * 0.001));
    this.animateTo(pos, target, fov, duration, 'top', onComplete);
  }

  /**
   * Smart side view — frames from tee box through all landing positions.
   * Camera positioned to see full trajectory from launch to rest.
   */
  smartSideView(points: Vec3[], duration = 0.7, onComplete?: () => void): void {
    if (points.length === 0) {
      this.setMobileBaselineView(true);
      return;
    }

    let maxZ = -Infinity;
    for (const p of points) {
      if (p.z > maxZ) maxZ = p.z;
    }

    // Frame from slightly behind tee to slightly past furthest ball
    const midZ = maxZ / 2;
    const rangeZ = maxZ * M_TO_YARDS;
    const camDist = Math.max(150, rangeZ * 0.65);
    const camHeight = Math.max(25, rangeZ * 0.12);

    const pos = new Vector3(-camDist, camHeight, midZ);
    const target = new Vector3(0, camHeight * 0.3, midZ);

    this.camera.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;
    const fov = Math.min(0.9, Math.max(0.55, 0.6 + rangeZ * 0.001));
    this.animateTo(pos, target, fov, duration, 'side', onComplete);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ── ORBIT ANCHOR (grounded pivot) ─────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  /** Lock the orbit pivot to a specific world point (tee, landing zone, etc). */
  setOrbitAnchor(point: Vec3 | Vector3): void {
    if (point instanceof Vector3) {
      this._orbitAnchor = point.clone();
    } else {
      this._orbitAnchor = new Vector3(point.x, point.y, point.z);
    }
    // Update the camera's orbit target so orbit gestures pivot around this point
    if (this.mode === 'freeLook') {
      this.camera.setTarget(this._orbitAnchor);
    }
  }

  /** Clear the orbit anchor — orbit freely. */
  clearOrbitAnchor(): void {
    this._orbitAnchor = null;
  }

  /** Anchor pivot to the tee box. */
  anchorToTee(): void {
    this.setOrbitAnchor(new Vector3(0, 0, 0));
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ── PHASE DEFAULT + DOUBLE-TAP RESET ──────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  /** Store the "home" camera position for the current phase. */
  setPhaseDefault(pos: Vec3 | Vector3, target: Vec3 | Vector3, fov: number): void {
    const p = pos instanceof Vector3 ? pos.clone() : new Vector3(pos.x, pos.y, pos.z);
    const t = target instanceof Vector3 ? target.clone() : new Vector3(target.x, target.y, target.z);
    this.phaseDefault = { pos: p, target: t, fov };
  }

  /** Smoothly animate back to the phase default view. */
  resetToPhaseDefault(): void {
    if (this.flightPoints.length) { this.fitFlights(); return; }
    if (!this.phaseDefault) {
      // Fallback: behind-tee view
      this.flybyToDownrange(0.6);
      return;
    }
    if (this.mode === 'transitioning') return; // don't interrupt an animation

    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;
    this.animateTo(
      this.phaseDefault.pos,
      this.phaseDefault.target,
      this.phaseDefault.fov,
      0.6,
      'freeLook',
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ── BROADCAST DIRECTOR (auto-cuts during multi-shot flight) ───────────────
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Start broadcast mode — automatic camera cuts during multi-shot flight.
   * Sequence: behind (launch) → side (ascent) → overhead (descent/landing).
   */
  startBroadcast(): void {
    this.mode = 'broadcast';
    this.broadcastElapsed = 0;
    this.broadcastCutPhase = 'launch';
    this.broadcastApexReached = false;
    this.maxBallHeight = 0;
    this.maxBallZ = 0;

    // Start from low behind-tee angle
    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;
    this.camera.fov = 0.95;
    this.smoothedPosition.set(0, 2.5, -6);
    this.smoothedTarget.set(0, 3, 50);
    this.camera.setPosition(this.smoothedPosition);
    this.camera.setTarget(this.smoothedTarget);
    this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
  }

  /**
   * Feed multi-ball positions to the broadcast director.
   * Called every physics frame during comparison/gear/ball testing.
   */
  updateBroadcast(dt: number, ballPositions: Vec3[]): void {
    if (this.mode !== 'broadcast' || ballPositions.length === 0) return;

    this.broadcastElapsed += dt;

    // Track aggregate stats
    let maxY = 0, maxZ = 0, avgX = 0, avgY = 0, avgZ = 0;
    for (const p of ballPositions) {
      if (p.y > maxY) maxY = p.y;
      if (p.z > maxZ) maxZ = p.z;
      avgX += p.x;
      avgY += p.y;
      avgZ += p.z;
    }
    avgX /= ballPositions.length;
    avgY /= ballPositions.length;
    avgZ /= ballPositions.length;

    if (maxY > this.maxBallHeight) this.maxBallHeight = maxY;
    if (maxZ > this.maxBallZ) this.maxBallZ = maxZ;

    // Detect apex: maxBallHeight was set and current max is falling
    if (!this.broadcastApexReached && this.maxBallHeight > 5 && maxY < this.maxBallHeight * 0.85) {
      this.broadcastApexReached = true;
    }

    // ── Cut logic ──
    if (this.broadcastCutPhase === 'launch' && this.broadcastElapsed > 1.5) {
      // Cut to side view for ascent arc
      this.broadcastCutPhase = 'ascent';
      this.camera.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;
      this.animateTo(
        new Vector3(-200, 30, avgZ + 30),
        new Vector3(0, 8, avgZ + 30),
        0.75,
        0.8,
        'broadcast',
      );
    } else if (this.broadcastCutPhase === 'ascent' && this.broadcastApexReached) {
      // Cut to elevated overhead for descent dispersion
      this.broadcastCutPhase = 'descent';
      this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;
      const lookZ = this.maxBallZ * M_TO_YARDS > 150 ? avgZ : avgZ + 20;
      this.animateTo(
        new Vector3(avgX, 60, lookZ - 20),
        new Vector3(avgX, 0, lookZ),
        0.9,
        1.0,
        'broadcast',
      );
    }

    // During launch phase: smooth tracking from behind
    if (this.broadcastCutPhase === 'launch' && !this.transitionActive) {
      const desiredTarget = new Vector3(avgX, avgY * 0.7, avgZ);
      const heightBoost = this.maxBallHeight * 0.3;
      const desiredPos = new Vector3(0, 2.5 + heightBoost, -6);

      const posAlpha = 1 - Math.pow(1 - 0.05, dt * 60);
      const targetAlpha = 1 - Math.pow(1 - 0.12, dt * 60);

      Vector3.LerpToRef(this.smoothedPosition, desiredPos, posAlpha, this.smoothedPosition);
      Vector3.LerpToRef(this.smoothedTarget, desiredTarget, targetAlpha, this.smoothedTarget);

      this.camera.setPosition(this.smoothedPosition);
      this.camera.setTarget(this.smoothedTarget);
    }
  }

  /** End broadcast mode — transition to user-controlled view. */
  endBroadcast(): void {
    if (this.mode === 'broadcast') {
      this.mode = 'freeLook';
      this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ── CORE MODE METHODS ─────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  private processKeyboard(dt: number): void {
    if (this.mode === 'behind' || this.mode === 'replay' || this.mode === 'transitioning') return;
    if (this.activeKeys.size === 0) return;

    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'SELECT' || active.tagName === 'TEXTAREA')) return;

    const forward = this.camera.getTarget().subtract(this.camera.position);
    const fwd = new Vector3(forward.x, 0, forward.z).normalize();
    const right = Vector3.Cross(Vector3.Up(), fwd).normalize();

    const move = new Vector3(0, 0, 0);
    const speed = this.FLY_SPEED * dt;

    if (this.activeKeys.has('ArrowUp') || this.activeKeys.has('KeyW')) move.addInPlace(fwd.scale(speed));
    if (this.activeKeys.has('ArrowDown') || this.activeKeys.has('KeyS')) move.addInPlace(fwd.scale(-speed));
    if (this.activeKeys.has('ArrowLeft') || this.activeKeys.has('KeyA')) move.addInPlace(right.scale(-speed));
    if (this.activeKeys.has('ArrowRight') || this.activeKeys.has('KeyD')) move.addInPlace(right.scale(speed));
    if (this.activeKeys.has('KeyE')) move.addInPlace(new Vector3(0, speed, 0));   // up
    if (this.activeKeys.has('KeyQ')) move.addInPlace(new Vector3(0, -speed, 0));  // down

    if (move.lengthSquared() > 0) {
      this.camera.target.addInPlace(move);
    }
  }

  getCamera(): Camera { return this.camera; }
  getMode(): CameraMode { return this.mode; }
  getModeLabel(): string { return CAMERA_LABELS[this.mode] || this.mode.toUpperCase(); }
  isInteractive(): boolean { return this.mode !== 'transitioning' && this.mode !== 'replay'; }

  setFreeLook(): void {
    this.mode = 'freeLook';
    this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
  }

  /**
   * Set shot camera mode. User controls stay attached.
   */
  setShotMode(mode?: 'behind' | 'side' | 'top'): void {
    this.mode = mode || 'behind';
    this.maxBallHeight = 0;
    this.maxBallZ = 0;
    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;
    this.camera.fov = 0.8;

    if (this.mode === 'behind') {
      this.smoothedPosition.set(0, 3.5, -8);
      this.smoothedTarget.set(0, 1, 10);
    } else if (this.mode === 'side') {
      this.smoothedPosition.set(-180, 35, 110);
      this.smoothedTarget.set(0, 10, 110);
    } else if (this.mode === 'top') {
      this.smoothedPosition.set(0, 150, 100);
      this.smoothedTarget.set(0, 0, 110);
    }

    this.camera.setPosition(this.smoothedPosition);
    this.camera.setTarget(this.smoothedTarget);
    this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
  }

  /** Animated view snap with smooth transition. */
  snapToView(mode: 'behind' | 'side' | 'top', shotActive = false): void {
    let pos: Vector3;
    let target: Vector3;
    let fov = 0.8;
    let fovMode = Camera.FOVMODE_VERTICAL_FIXED;

    if (mode === 'behind') {
      const heightBoost = this.maxBallHeight * 0.35;
      pos = new Vector3(this.behindOffsetX, this.behindBaseY + heightBoost, this.behindBaseZ);
      target = new Vector3(0, 1, 10);
    } else if (mode === 'side') {
      pos = new Vector3(-280, 40, 90);
      target = new Vector3(0, 8, 90);
      fov = 0.75;
      fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;
    } else {
      pos = new Vector3(0, 200, 120);
      target = new Vector3(0, 0, 120);
    }

    this.camera.fovMode = fovMode;

    if (mode === 'behind' && shotActive) {
      this.mode = 'behind';
      this.smoothedPosition.copyFrom(pos);
      this.smoothedTarget.copyFrom(target);
      this.camera.fov = fov;
      this.camera.setPosition(pos);
      this.camera.setTarget(target);
      this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
      return;
    }

    this.animateTo(pos, target, fov, 0.6, mode === 'behind' ? 'freeLook' : mode);
  }

  /** Downrange view for mobile — animated transition. */
  setMobileDownrangeView(animate = true): void {
    if (this.flightPoints.length) { this.fitFlights('behind', animate); return; }
    if (this.flightFramePending) return;
    this.behindOffsetX = 4;
    this.behindBaseY = 8;
    this.behindBaseZ = -14;
    this.maxBallHeight = 0;
    this.maxBallZ = 0;
    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;

    const pos = new Vector3(4, 8, -14);
    const target = new Vector3(0, 2, 80);

    if (animate && this.mode !== 'behind' && this.mode !== 'freeLook') {
      this.animateTo(pos, target, 0.95, 0.7, 'freeLook');
    } else {
      this.mode = 'freeLook';
      this.camera.fov = 0.95;
      this.smoothedPosition.copyFrom(pos);
      this.smoothedTarget.copyFrom(target);
      this.camera.setPosition(pos);
      this.camera.setTarget(target);
      this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
    }
  }

  /** Tight side view for mobile — animated. */
  setMobileBaselineView(animate = true): void {
    if (this.flightPoints.length) { this.fitFlights('side', animate); return; }
    this.maxBallHeight = 0;
    this.maxBallZ = 0;
    this.camera.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;

    const pos = new Vector3(-280, 40, 90);
    const target = new Vector3(0, 8, 90);

    if (animate && this.mode !== 'side') {
      this.animateTo(pos, target, 0.75, 0.7, 'side');
    } else {
      this.mode = 'side';
      this.camera.fov = 0.75;
      this.smoothedPosition.copyFrom(pos);
      this.smoothedTarget.copyFrom(target);
      this.camera.setPosition(pos);
      this.camera.setTarget(target);
      this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
    }
  }

  /**
   * Mobile shot-watching view.
   * Keeps the tee, apex band, tracer, and landing corridor visible above the
   * collapsed bottom drawer during baseline/spec/gear/ball flight phases.
   */
  setMobileShotPresentationView(
    animate = true,
    phase: MobileShotPresentationPhase = 'baseline',
    clubKey = 'driver',
  ): void {
    this.mode = 'side';
    this.maxBallHeight = 0;
    this.maxBallZ = 0;
    this.behindOffsetX = 4;
    this.behindBaseY = 8;
    this.behindBaseZ = -14;
    this.camera.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;

    const { pos, target, fov } = getMobileShotViewPreset(phase, clubKey);

    if (animate) {
      this.animateTo(pos, target, fov, 0.45, 'side');
    } else {
      this.camera.fov = fov;
      this.smoothedPosition.copyFrom(pos);
      this.smoothedTarget.copyFrom(target);
      this.camera.setPosition(pos);
      this.camera.setTarget(target);
      this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
    }
  }

  /** Bird's-eye top-down view — animated. */
  setMobileTopView(animate = true): void {
    if (this.flightPoints.length) { this.fitFlights('top', animate); return; }
    this.maxBallHeight = 0;
    this.maxBallZ = 0;
    this.camera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;

    const pos = new Vector3(0, 200, 120);
    const target = new Vector3(0, 0, 120);

    if (animate && this.mode !== 'top') {
      this.animateTo(pos, target, 0.8, 0.7, 'top');
    } else {
      this.mode = 'top';
      this.camera.fov = 0.8;
      this.smoothedPosition.copyFrom(pos);
      this.smoothedTarget.copyFrom(target);
      this.camera.setPosition(pos);
      this.camera.setTarget(target);
      this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
    }
  }

  /** Side view for comparison mode. */
  setComparisonSideView(): void {
    this.mode = 'side';
    this.maxBallHeight = 0;
    this.maxBallZ = 0;
    this.camera.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;
    this.camera.fov = 0.75;
    this.smoothedPosition.set(-280, 40, 90);
    this.smoothedTarget.set(0, 8, 90);
    this.camera.setPosition(this.smoothedPosition);
    this.camera.setTarget(this.smoothedTarget);
    this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
  }

  setReplay(): void {
    this.mode = 'replay';
    this.camera.detachControl();
  }

  update(dt: number, ballPos: Vec3, ballVel: Vec3): void {
    if (this.mode === 'freeLook' || this.mode === 'transitioning' || this.mode === 'broadcast') return;

    const bp = new Vector3(ballPos.x, ballPos.y, ballPos.z);
    const bv = new Vector3(ballVel.x, ballVel.y, ballVel.z);

    if (bp.y > this.maxBallHeight) this.maxBallHeight = bp.y;
    if (bp.z > this.maxBallZ) this.maxBallZ = bp.z;

    if (this.mode === 'behind') {
      this.updateBehind(dt, bp);
    } else if (this.mode === 'side') {
      this.updateSide(dt, bp);
    } else if (this.mode === 'top') {
      this.updateTop(dt, bp);
    } else if (this.mode === 'replay') {
      this.updateReplay(dt, bp, bv);
    }
  }

  private updateBehind(dt: number, ballPos: Vector3): void {
    const heightBoost = this.maxBallHeight * 0.35;
    const desiredPos = new Vector3(this.behindOffsetX, this.behindBaseY + heightBoost, this.behindBaseZ);
    const targetY = ballPos.y * 0.6;
    const desiredTarget = new Vector3(ballPos.x, targetY, ballPos.z);

    const posAlpha = 1 - Math.pow(1 - 0.04, dt * 60);
    const targetAlpha = 1 - Math.pow(1 - 0.08, dt * 60);

    Vector3.LerpToRef(this.smoothedPosition, desiredPos, posAlpha, this.smoothedPosition);
    Vector3.LerpToRef(this.smoothedTarget, desiredTarget, targetAlpha, this.smoothedTarget);

    this.camera.setPosition(this.smoothedPosition);
    this.camera.setTarget(this.smoothedTarget);
  }

  private updateSide(_dt: number, _ballPos: Vector3): void { /* fixed tripod */ }
  private updateTop(_dt: number, _ballPos: Vector3): void { /* fixed tripod */ }

  private updateReplay(dt: number, ballPos: Vector3, ballVel: Vector3): void {
    const speed = ballVel.length();
    const velDir = speed > 0.5 ? ballVel.normalize() : new Vector3(0, 0, 1);

    const desiredPos = new Vector3(
      ballPos.x - velDir.x * 8,
      ballPos.y + 3.5,
      ballPos.z - velDir.z * 8,
    );
    const desiredTarget = new Vector3(
      ballPos.x + velDir.x * 5,
      ballPos.y * 0.8,
      ballPos.z + velDir.z * 5,
    );

    const posAlpha = 1 - Math.pow(1 - 0.06, dt * 60);
    const targetAlpha = 1 - Math.pow(1 - 0.10, dt * 60);

    Vector3.LerpToRef(this.smoothedPosition, desiredPos, posAlpha, this.smoothedPosition);
    Vector3.LerpToRef(this.smoothedTarget, desiredTarget, targetAlpha, this.smoothedTarget);

    this.camera.setPosition(this.smoothedPosition);
    this.camera.setTarget(this.smoothedTarget);
  }

  snapTo(position: Vec3, target: Vec3): void {
    this.smoothedPosition.set(position.x, position.y, position.z);
    this.smoothedTarget.set(target.x, target.y, target.z);
    this.camera.setPosition(this.smoothedPosition);
    this.camera.setTarget(this.smoothedTarget);
  }

  cancelTransition(): void {
    if (this.transitionActive) {
      this.transitionActive = false;
      this.mode = this.transitionPostMode;
      this.camera.attachControl(this.scene.getEngine().getRenderingCanvas()!, true);
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────────

  /** Set camera to a position without animating (used to set start of animation). */
  private setStartPosition(pos: Vector3, target: Vector3): void {
    this.camera.setPosition(pos);
    this.camera.setTarget(target);
    this.smoothedPosition.copyFrom(pos);
    this.smoothedTarget.copyFrom(target);
  }

  // ── Debug overlay ───────────────────────────────────────────────────────
  private debugFrameSkip = 0;
  private updateDebugOverlay(): void {
    // Update every 10 frames to avoid perf impact
    this.debugFrameSkip++;
    if (this.debugFrameSkip % 10 !== 0) return;

    const el = document.getElementById('camera-debug');
    if (!el) return;

    const p = this.camera.position;
    const t = this.camera.getTarget();
    const r = (n: number) => Math.round(n * 10) / 10;

    el.textContent =
      `mode: ${this.mode}\n` +
      `pos:  (${r(p.x)}, ${r(p.y)}, ${r(p.z)})\n` +
      `tgt:  (${r(t.x)}, ${r(t.y)}, ${r(t.z)})\n` +
      `fov:  ${r(this.camera.fov)}  radius: ${r(this.camera.radius)}\n` +
      `alpha: ${r(this.camera.alpha)}  beta: ${r(this.camera.beta)}`;
  }
}
